import { useEffect, useState } from 'react';
import {
  ChevronDown, ChevronRight, AlertTriangle, CheckCircle2, XCircle, Send, Loader2, Ban, RefreshCw,
} from 'lucide-react';
import {
  sendChargerCommand, fetchChargerReservations, type ChargerReservation,
} from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import formStyles from '@/components/ui/Form.module.css';
import {
  COMMAND_DEFS, COMMAND_GROUPS,
  type CommandSpec, type CommandField,
} from './commandCatalogue';
import styles from './ChargerDetailPage.module.css';

interface CommandsPanelProps {
  chargerId: string;
  chargerIdentity: string;
  isOnline: boolean;
  /** Pre-formatted "4h ago" from the caller's own timeAgo, or null when the
   *  charger has never sent a heartbeat. Only shown while offline, where
   *  "how long ago" is the fact that tells an operator whether to wait or
   *  go and look at the unit. */
  lastSeen: string | null;
}

interface DispatchResult {
  ok: boolean;
  commandId?: string;
  acceptedAt?: string;
  chargerResponse?: unknown;
  sentPayload?: unknown;
  error?: string;
  code?: string;
  hint?: string;
}

/** A charger that answers CALLERROR NotImplemented/NotSupported is telling
 *  us the truth about its firmware — render that as a neutral fact, never
 *  as a red app error. */
function isNotSupported(result: DispatchResult): boolean {
  return !result.ok
    && result.code === 'OCPP_DISPATCH_FAILED'
    && /NotImplemented|NotSupported/i.test(result.error ?? '');
}

/**
 * Operator command surface. Renders one collapsible card per command,
 * mirrors the API catalogue 1:1 — when this drifts from the API we
 * lose the agent/UI parity guarantee.
 *
 * Destructive commands gate the dispatch behind a confirm modal;
 * Update Firmware additionally requires TYPING THE CHARGER IDENTITY —
 * a wrong image can permanently brick hardware.
 */
export function CommandsPanel({
  chargerId, chargerIdentity, isOnline, lastSeen,
}: CommandsPanelProps) {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [result, setResult] = useState<{ key: string; result: DispatchResult } | null>(null);
  const [confirm, setConfirm] = useState<{ spec: CommandSpec; body: Record<string, unknown> } | null>(null);
  const [confirmText, setConfirmText] = useState('');

  // Group definitions by their `group` for the section headers.
  const grouped = COMMAND_GROUPS.map((g) => ({
    ...g,
    commands: COMMAND_DEFS.filter((c) => c.group === g.key),
  }));

  const dispatch = async (spec: CommandSpec, body: Record<string, unknown>) => {
    setPending(spec.key);
    setResult(null);
    try {
      const data = await sendChargerCommand(chargerId, spec.key, body);
      setResult({
        key: spec.key,
        result: {
          ok: true,
          commandId: data.commandId,
          acceptedAt: data.acceptedAt,
          chargerResponse: data.chargerResponse,
          sentPayload: data.sentPayload,
        },
      });
    } catch (err: unknown) {
      // ky throws HTTPError on non-2xx; pull JSON body if present.
      const httpErr = err as { response?: Response; message?: string };
      let payload: DispatchResult = { ok: false, error: httpErr.message ?? 'Unknown error' };
      if (httpErr.response) {
        try {
          const j = await httpErr.response.clone().json() as {
            error?: string; code?: string; hint?: string; commandId?: string;
          };
          payload = {
            ok: false,
            error: j.error ?? 'Request failed',
            ...(j.code !== undefined ? { code: j.code } : {}),
            ...(j.hint !== undefined ? { hint: j.hint } : {}),
            ...(j.commandId !== undefined ? { commandId: j.commandId } : {}),
          };
        } catch {
          // ignore — keep generic
        }
      }
      setResult({ key: spec.key, result: payload });
    } finally {
      setPending(null);
    }
  };

  const onSubmit = (spec: CommandSpec, body: Record<string, unknown>) => {
    if (spec.destructive) {
      setConfirmText('');
      setConfirm({ spec, body });
      return;
    }
    void dispatch(spec, body);
  };

  // Update Firmware demands the typed identity; other destructive commands
  // keep the button-confirm they had.
  const needsTypedConfirm = confirm?.spec.key === 'update-firmware';
  const typedConfirmOk = !needsTypedConfirm || confirmText.trim() === chargerIdentity;

  return (
    <div className={styles.cmdPanel}>
      <p className={styles.cmdLead}>
        Direct OCPP commands. Identity: <code>{chargerIdentity}</code>.
        {!isOnline && (
          <span className={styles.cmdOfflineHint}>
            {' '}<AlertTriangle size={12} /> Charger is offline — commands are disabled.
          </span>
        )}
      </p>

      {grouped.map((g) => g.commands.length > 0 && (
        <div key={g.key} className={styles.cmdGroup}>
          <h3 className={styles.cmdGroupTitle}>{g.label}</h3>
          <div className={styles.cmdGroupBody}>
            {g.commands.map((spec) => (
              <CommandCard
                key={spec.key}
                spec={spec}
                isOpen={openKey === spec.key}
                onToggle={() => setOpenKey(openKey === spec.key ? null : spec.key)}
                pending={pending === spec.key}
                result={result?.key === spec.key ? result.result : null}
                onSubmit={(body) => onSubmit(spec, body)}
                offline={!isOnline}
                lastSeen={lastSeen}
                extra={
                  (spec.key === 'reserve-now' || spec.key === 'cancel-reservation') ? (
                    <ReservationsList
                      chargerId={chargerId}
                      refreshToken={result?.key === spec.key ? result.result : null}
                    />
                  ) : null
                }
              />
            ))}
          </div>
        </div>
      ))}

      <Modal
        isOpen={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        title={`Confirm ${confirm?.spec.label ?? ''}`}
        size="sm"
      >
        {confirm && (
          <div className={styles.confirmBody}>
            <p className={styles.confirmLead}>
              <AlertTriangle size={16} className={styles.confirmIcon} />
              {needsTypedConfirm
                ? 'A wrong firmware image can PERMANENTLY damage this charger. '
                  + 'The update cannot be stopped once the download starts.'
                : 'This action affects the live charger. Proceed?'}
            </p>
            <pre className={styles.confirmJson}>{JSON.stringify(confirm.body, null, 2)}</pre>
            {needsTypedConfirm && (
              <div className={formStyles.field}>
                <label className={`${formStyles.label} ${formStyles.required}`}>
                  Type the charger identity ({chargerIdentity}) to confirm
                </label>
                <input
                  className={formStyles.input}
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={chargerIdentity}
                  autoComplete="off"
                />
              </div>
            )}
            <div className={styles.confirmActions}>
              <button className={formStyles.btnSecondary} onClick={() => setConfirm(null)}>
                Cancel
              </button>
              <button
                className={formStyles.btnPrimary}
                disabled={!typedConfirmOk}
                onClick={() => {
                  const c = confirm;
                  setConfirm(null);
                  void dispatch(c.spec, c.body);
                }}
              >
                Dispatch
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ── CommandCard ────────────────────────────────────────────────────

interface CommandCardProps {
  spec: CommandSpec;
  isOpen: boolean;
  onToggle: () => void;
  pending: boolean;
  result: DispatchResult | null;
  onSubmit: (body: Record<string, unknown>) => void;
  offline: boolean;
  lastSeen: string | null;
  /** Command-specific companion UI (the reservations ledger). */
  extra?: React.ReactNode;
}

function CommandCard({
  spec, isOpen, onToggle, pending, result, onSubmit, offline, lastSeen, extra,
}: CommandCardProps) {
  return (
    <div
      className={`${styles.cmdCard} ${styles[`cmdAccent_${spec.accent}`] ?? ''} ${spec.implemented ? '' : styles.cmdCardStub}`}
    >
      <button className={styles.cmdCardHeader} onClick={onToggle} type="button">
        <span className={styles.cmdCardIcon}><spec.icon size={16} /></span>
        <span className={styles.cmdCardTitle}>
          {spec.label}
          {!spec.implemented && <span className={styles.cmdComingSoon}>Coming soon</span>}
          {spec.destructive && <span className={styles.cmdDestructive}>Destructive</span>}
        </span>
        {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>

      {isOpen && (
        <div className={styles.cmdCardBody}>
          <p className={styles.cmdCardDesc}>{spec.description}</p>

          {spec.fields.length === 0 && spec.implemented && (
            <p className={styles.cmdCardNoFields}>No parameters required.</p>
          )}

          <CommandForm
            spec={spec}
            disabled={!spec.implemented || pending}
            offline={offline}
            lastSeen={lastSeen}
            onSubmit={onSubmit}
          />

          {pending && (
            <p className={styles.cmdPending}>
              <Loader2 size={14} className={styles.cmdSpinner} /> Dispatching…
            </p>
          )}

          {result && <CommandResultView spec={spec} result={result} />}
          {extra}
        </div>
      )}
    </div>
  );
}

// ── CommandForm ────────────────────────────────────────────────────

interface CommandFormProps {
  spec: CommandSpec;
  disabled: boolean;
  offline: boolean;
  lastSeen: string | null;
  onSubmit: (body: Record<string, unknown>) => void;
}

function CommandForm({ spec, disabled, offline, lastSeen, onSubmit }: CommandFormProps) {
  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const f of spec.fields) {
      initial[f.key] = f.defaultValue !== undefined ? String(f.defaultValue) : '';
    }
    return initial;
  });

  const setVal = (k: string, v: string) => setValues((s) => ({ ...s, [k]: v }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const body: Record<string, unknown> = {};
    for (const f of spec.fields) {
      const raw = values[f.key];
      if (raw === '' || raw === undefined) continue;
      if (f.type === 'number') {
        const n = Number(raw);
        if (!Number.isNaN(n)) body[f.key] = n;
      } else {
        body[f.key] = raw;
      }
    }
    onSubmit(body);
  };

  // An offline charger cannot be commanded at all, so the button says so
  // instead of offering a dispatch that would only fail at the OCPP hop.
  // "Not implemented" wins when both apply: that one is permanent, and a
  // charger coming back online would not change it.
  const blockedByOffline = offline && spec.implemented;
  const label = !spec.implemented
    ? 'Not implemented'
    : offline ? 'Charger offline' : 'Dispatch';

  return (
    <form className={styles.cmdForm} onSubmit={handleSubmit}>
      {spec.fields.map((f) => (
        <FieldInput
          key={f.key}
          field={f}
          value={values[f.key] ?? ''}
          onChange={(v) => setVal(f.key, v)}
          disabled={disabled || offline}
        />
      ))}
      <button
        type="submit"
        className={formStyles.btnPrimary}
        disabled={disabled || offline || spec.fields.some((f) => f.required && !values[f.key])}
      >
        <Send size={13} /> {label}
      </button>
      {blockedByOffline && (
        <p className={styles.cmdCardNoFields}>
          Commands need a live connection.
          {lastSeen
            ? ` This charger last checked in ${lastSeen}.`
            : ' This charger has never checked in.'}
        </p>
      )}
    </form>
  );
}

function FieldInput({
  field, value, onChange, disabled,
}: {
  field: CommandField;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
}) {
  return (
    <div className={formStyles.field}>
      <label className={`${formStyles.label} ${field.required ? formStyles.required : ''}`}>
        {field.label}
      </label>
      {field.type === 'enum' ? (
        <select
          className={formStyles.input}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        >
          {!field.required && <option value="">— None —</option>}
          {(field.options ?? []).map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      ) : (
        <input
          className={formStyles.input}
          type={field.type === 'number' ? 'number' : 'text'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          disabled={disabled}
          required={field.required}
        />
      )}
      {field.hint && <p className={formStyles.hint}>{field.hint}</p>}
    </div>
  );
}

// ── Result ────────────────────────────────────────────────────────

/** Server-built values worth echoing back per command — the allocated
 *  reservation id, the pushed list version + entry count. Everything else
 *  skips the echo (the raw response pre is enough). */
function summarizeSentPayload(specKey: string, sent: unknown): string | null {
  if (sent === null || typeof sent !== 'object') return null;
  const s = sent as Record<string, unknown>;
  if (specKey === 'reserve-now' && typeof s.reservationId === 'number') {
    return `Allocated reservation id ${s.reservationId} (connector ${String(s.connectorId ?? 0)}, holds until ${String(s.expiryDate ?? '?')})`;
  }
  if (specKey === 'send-local-list') {
    const entries = Array.isArray(s.localAuthorizationList) ? s.localAuthorizationList.length : 0;
    return `Pushed list version ${String(s.listVersion ?? '?')} with ${entries} card${entries === 1 ? '' : 's'} (full replace)`;
  }
  return null;
}

function CommandResultView({ spec, result }: { spec: CommandSpec; result: DispatchResult }) {
  // CHARGER_NOT_FOUND is the one failure the raw server copy cannot explain.
  // The 404 deliberately conflates "no such charger" with "not yours" so it
  // never confirms existence, which means the server hint ("Pass either the
  // Mongo ObjectId or the OCPP identity string") reads as a caller mistake
  // when the caller did nothing wrong — the page it is sitting on supplied
  // the id. The SERVER RESPONSE IS UNCHANGED; only what an operator reads
  // is. Reload is the honest next step: if the charger was deleted or moved
  // out of scope, the detail page itself will say so.
  if (!result.ok && result.code === 'CHARGER_NOT_FOUND') {
    return (
      <div className={`${styles.cmdResult} ${styles.cmdResultErr}`}>
        <div className={styles.cmdResultHead}>
          <XCircle size={14} /> Charger unavailable
        </div>
        <p className={styles.cmdResultMsg}>
          This charger is no longer available to you. It may have been moved,
          deleted, or is outside your access.
        </p>
        <div>
          <button
            type="button"
            className={formStyles.btnSecondary}
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }

  // A reset the charger did not acknowledge is, in practice, a reset that
  // happened: some firmware restarts before it replies (the Autel DC
  // Compact did, in the 2026-09-17 staging tests). Neutral, with the one
  // signal that settles it, rather than a red failure.
  if (!result.ok && result.code === 'RESET_UNACKNOWLEDGED') {
    return (
      <div className={`${styles.cmdResult} ${styles.cmdResultInfo}`}>
        <div className={styles.cmdResultHead}>
          <RefreshCw size={14} /> Reset sent, not acknowledged
          {result.commandId && (
            <code className={styles.cmdResultId}>cmd: {result.commandId}</code>
          )}
        </div>
        <p className={styles.cmdResultMsg}>
          The charger did not reply before restarting. Watch the Logs tab: a BootNotification
          within the next minute means it rebooted.
        </p>
      </div>
    );
  }

  // A charger honestly saying "I don't do this" is a fact, not a failure.
  if (isNotSupported(result)) {
    return (
      <div className={`${styles.cmdResult} ${styles.cmdResultInfo}`}>
        <div className={styles.cmdResultHead}>
          <Ban size={14} /> Not supported by this charger
          {result.commandId && (
            <code className={styles.cmdResultId}>cmd: {result.commandId}</code>
          )}
        </div>
        <p className={styles.cmdResultMsg}>
          The charger reports its firmware does not implement {spec.label}. Nothing was changed.
        </p>
        {result.error && <p className={styles.cmdResultHint}>{result.error}</p>}
      </div>
    );
  }

  const sentSummary = result.ok ? summarizeSentPayload(spec.key, result.sentPayload) : null;

  return (
    <div className={`${styles.cmdResult} ${result.ok ? styles.cmdResultOk : styles.cmdResultErr}`}>
      <div className={styles.cmdResultHead}>
        {result.ok
          ? <><CheckCircle2 size={14} /> Accepted</>
          : <><XCircle size={14} /> {result.code ?? 'Failed'}</>}
        {result.commandId && (
          <code className={styles.cmdResultId}>cmd: {result.commandId}</code>
        )}
      </div>
      {!result.ok && result.error && <p className={styles.cmdResultMsg}>{result.error}</p>}
      {!result.ok && result.hint && <p className={styles.cmdResultHint}>{result.hint}</p>}
      {sentSummary && <p className={styles.cmdResultMsg}>{sentSummary}</p>}
      {result.ok && spec.key === 'get-composite-schedule' ? (
        <CompositeScheduleView response={result.chargerResponse} />
      ) : (
        result.ok && result.chargerResponse !== undefined && (
          <pre className={styles.cmdResultPayload}>
            {JSON.stringify(result.chargerResponse, null, 2)}
          </pre>
        )
      )}
    </div>
  );
}

// ── Composite schedule renderer ───────────────────────────────────

interface CompositeSchedule {
  status?: string;
  connectorId?: number;
  scheduleStart?: string;
  chargingSchedule?: {
    duration?: number;
    startSchedule?: string;
    chargingRateUnit?: string;
    chargingSchedulePeriod?: Array<{
      startPeriod?: number;
      limit?: number;
      numberPhases?: number;
    }>;
  };
}

/** GetCompositeSchedule.conf, rendered as a readable limit timeline
 *  instead of raw JSON. Anything shapeless falls back to the JSON pre. */
function CompositeScheduleView({ response }: { response: unknown }) {
  const r = (response ?? {}) as CompositeSchedule;
  const schedule = r.chargingSchedule;
  const periods = schedule?.chargingSchedulePeriod;

  if (r.status !== 'Accepted' || !schedule || !Array.isArray(periods) || periods.length === 0) {
    // Rejected, or accepted-but-empty — show the honest raw payload.
    return (
      <pre className={styles.cmdResultPayload}>
        {JSON.stringify(response, null, 2)}
      </pre>
    );
  }

  const unit = schedule.chargingRateUnit === 'W' ? 'W' : 'A';
  const fmtOffset = (s: number) => {
    if (s < 60) return `+${s}s`;
    if (s < 3600) return `+${Math.floor(s / 60)}m${s % 60 ? ` ${s % 60}s` : ''}`;
    return `+${Math.floor(s / 3600)}h${Math.floor((s % 3600) / 60) ? ` ${Math.floor((s % 3600) / 60)}m` : ''}`;
  };

  return (
    <div className={styles.cmdSchedule}>
      <p className={styles.cmdScheduleMeta}>
        Connector {r.connectorId ?? '?'}
        {schedule.startSchedule ? ` · from ${new Date(schedule.startSchedule).toLocaleString()}` : ''}
        {typeof schedule.duration === 'number' ? ` · window ${fmtOffset(schedule.duration).slice(1)}` : ''}
      </p>
      <table className={styles.cmdScheduleTable}>
        <thead>
          <tr><th>From</th><th>Limit</th><th>Phases</th></tr>
        </thead>
        <tbody>
          {periods.map((p, i) => (
            <tr key={i}>
              <td>{fmtOffset(p.startPeriod ?? 0)}</td>
              <td>{typeof p.limit === 'number' ? `${p.limit} ${unit}` : '—'}</td>
              <td>{p.numberPhases ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Reservations ledger ───────────────────────────────────────────

/** The charger's reservation ledger, shown under Reserve Now and Cancel
 *  Reservation so the allocated ids survive a page reload. Refetches when
 *  either command lands a result. */
function ReservationsList({
  chargerId, refreshToken,
}: {
  chargerId: string;
  refreshToken: unknown;
}) {
  const [rows, setRows] = useState<ChargerReservation[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchChargerReservations(chargerId)
      .then((r) => { if (!cancelled) setRows(r); })
      .catch(() => { if (!cancelled) setRows([]); });
    return () => { cancelled = true; };
  }, [chargerId, refreshToken]);

  if (rows === null) return null;
  if (rows.length === 0) {
    return <p className={styles.cmdCardNoFields}>No reservations recorded for this charger.</p>;
  }
  return (
    <div className={styles.cmdSchedule}>
      <p className={styles.cmdScheduleMeta}>Reservations (newest first)</p>
      <table className={styles.cmdScheduleTable}>
        <thead>
          <tr><th>Id</th><th>Connector</th><th>idTag</th><th>Holds until</th><th>State</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.reservationId}>
              <td>{r.reservationId}</td>
              <td>{r.connectorId === 0 ? 'any' : r.connectorId}</td>
              <td>{r.idTag === null ? '—' : <code>{r.idTag}</code>}</td>
              <td>{new Date(r.expiryDate).toLocaleString()}</td>
              <td>{r.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
